import { useState, type FormEvent } from 'react'
import { api } from '../lib/api'
import { authClient, authMessage } from '../lib/authClient'
import type { Session } from '@shared/types'

type Mode = 'signin' | 'forgot' | 'sent'

/**
 * Email and password. "Forgot password" is also how a brand-new account sets
 * its first one: the migration that created accounts for existing families
 * gave them no password, and the reset link creates the credential.
 */
export function SignIn({ onSignedIn, intro }: { onSignedIn: (session: Session) => void; intro?: string }) {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (mode === 'forgot') {
        const { error } = await authClient.requestPasswordReset({
          email: email.trim(),
          redirectTo: '/reset-password',
        })
        if (error) throw error
        setMode('sent')
        return
      }
      const { error } = await authClient.signIn.email({ email: email.trim(), password })
      if (error) throw error
      // The session as the app understands it -- with the family -- comes from
      // our own /me, not from Better Auth's answer.
      const me = await api.me()
      onSignedIn(me.session)
    } catch (err) {
      setError(authMessage(err as { message?: string; code?: string }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="lock">
      <form className="lock-card" onSubmit={submit}>
        <div className="lock-logo" aria-hidden="true">
          🥪
        </div>
        <h1>Five Lunches</h1>
        <p className="sub">
          {mode === 'forgot'
            ? 'We’ll email you a link to set a new one.'
            : mode === 'sent'
              ? 'Check your email.'
              : (intro ?? 'Plan the week’s lunches.')}
        </p>

        <div className="stack">
          {error && <div className="banner error">{error}</div>}

          {mode === 'sent' ? (
            <>
              <p className="muted">
                If there’s an account for <b>{email.trim()}</b>, a link to set your password is on its way. It
                works once and expires in an hour.
              </p>
              <button className="btn" type="button" onClick={() => setMode('signin')}>
                Back to sign in
              </button>
            </>
          ) : (
            <>
              <div className="field">
                <label htmlFor="signin-email">Email</label>
                <input
                  id="signin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  autoFocus
                  required
                />
              </div>

              {mode === 'signin' && (
                <div className="field">
                  <label htmlFor="signin-pass">Password</label>
                  <input
                    id="signin-pass"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
              )}

              <button className="btn primary" type="submit" disabled={busy || !email || (mode === 'signin' && !password)}>
                {busy ? 'One moment…' : mode === 'forgot' ? 'Email me a link' : 'Sign in'}
              </button>

              <button
                className="btn ghost sm"
                type="button"
                onClick={() => {
                  setError(null)
                  setMode(mode === 'forgot' ? 'signin' : 'forgot')
                }}
              >
                {mode === 'forgot' ? 'Back to sign in' : 'Forgot password, or setting one for the first time?'}
              </button>

              {mode === 'signin' && !intro && (
                <p className="muted signup-cta">
                  New here? <a href="/signup">Sign up free</a>
                </p>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  )
}
